import { useState } from "react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { toast } from "sonner";
import { Share2, Link2, Mail, MessageCircle, Send, Linkedin, Facebook, Twitter } from "lucide-react";
import { SITE_URL } from "../lib/seo";

const DEFAULT_TEXT = "I use DentiCision — evidence-based clinical decision support for treating medically complex dental patients. Have a look:";

export function ShareButton({ path = "/", text = DEFAULT_TEXT, label = "Share", variant = "outline", className = "", testid = "share-btn" }) {
  const [open, setOpen] = useState(false);
  const url = `${SITE_URL}${path}`;
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text);
  const links = [
    { name: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${t}%20${u}` },
    { name: "X (Twitter)", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${t}&url=${u}` },
    { name: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { name: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { name: "Telegram", icon: Send, href: `https://t.me/share/url?url=${u}&text=${t}` },
    { name: "Email", icon: Mail, href: `mailto:?subject=${encodeURIComponent("DentiCision — clinical decision support for dentists")}&body=${t}%20${u}` },
  ];

  const nativeShare = async (e) => {
    if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
      e.preventDefault();
      try { await navigator.share({ title: "DentiCision", text, url }); } catch { /* cancelled */ }
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant={variant} data-testid={testid} onClick={nativeShare} className={className}>
          <Share2 className="w-4 h-4 mr-2" /> {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1.5" data-testid={`${testid}-menu`}>
        {links.map((l) => (
          <a key={l.name} href={l.href} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}
            data-testid={`${testid}-${l.name.split(" ")[0].toLowerCase()}`}
            className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-100">
            <l.icon className="w-4 h-4 text-slate-500" /> {l.name}
          </a>
        ))}
        <button type="button" onClick={copy} data-testid={`${testid}-copy`} className="w-full flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-100">
          <Link2 className="w-4 h-4 text-slate-500" /> Copy link
        </button>
      </PopoverContent>
    </Popover>
  );
}
